/**
 * Клиент раздела «Филиалы» (серверные endpoints /api/branches/*).
 *
 * Отдельно от repo.ts, потому что это не CRUD-зеркало таблицы, а операции подключения:
 * подтянуть города из Битрикса, связать город с филиалом, привязать рабочую группу
 * Telegram, удалить филиал с подтверждением.
 */
import { authHeaders, notifyUnauthorized } from '@/auth/session';
import type { Branch } from '@/types';

const BASE = '/api';

async function req<T>(method: string, path: string, body?: unknown): Promise<T> {
  const res = await fetch(`${BASE}${path}`, {
    method,
    headers: {
      Accept: 'application/json',
      ...(body !== undefined ? { 'Content-Type': 'application/json' } : {}),
      ...authHeaders(),
    },
    ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
  });
  if (res.status === 401) notifyUnauthorized();
  const json = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error((json as any)?.error || `HTTP ${res.status}`);
  return json as T;
}

/** Город из справочника Битрикса, которого ещё нет в календаре. */
export interface UnlinkedCity {
  enum_id: string;
  name: string;
}

/** Города, заведённые в Битриксе, но не подключённые к календарю. */
export const fetchUnlinkedCities = () =>
  req<{ items: UnlinkedCity[] }>('GET', '/branches/unlinked').then((r) => r.items ?? []);

/**
 * Подключить город: завести филиал и связать его с городом из Битрикса.
 * Часовой пояс задаём смещением от UTC в минутах (tz_offset_minutes) — именованную
 * зону сервер выведет сам. deals_fixed — сколько игр, висевших «без филиала», встали на место.
 */
export const linkBranch = (input: { enum_id: string; name: string; tz_offset_minutes: number; color?: string }) =>
  req<{ branch: Branch; deals_fixed: number }>('POST', '/branches/link', input);

/** Одноразовый код привязки рабочей группы Telegram (живёт 10 минут). */
export interface TelegramCode {
  code: string;
  /** Момент истечения кода — epoch ms (сервер отдаёт число, не строку). */
  expires_at: number;
  bot_username: string;
}

export const requestBranchTelegramCode = (id: string) =>
  req<TelegramCode>('POST', `/branches/${id}/telegram-code`);

/** Отвязать рабочую группу Telegram от филиала. */
export const unlinkBranchTelegram = (id: string) =>
  req<{ ok: true }>('DELETE', `/branches/${id}/telegram`);

/**
 * Ошибка «к филиалу привязаны игры» — сервер отвечает 409 и говорит, сколько их.
 * Отдельный класс, чтобы экран мог спросить подтверждение и повторить с force.
 */
export class BranchHasDealsError extends Error {
  constructor(public readonly dealsCount: number, message: string) {
    super(message);
    this.name = 'BranchHasDealsError';
  }
}

/**
 * Удалить филиал. Без force сервер откажет (409), если к филиалу привязаны игры —
 * тогда бросаем BranchHasDealsError, спрашиваем человека и повторяем с force = true.
 */
export async function deleteBranch(id: string, force = false): Promise<void> {
  const res = await fetch(`${BASE}/branches/${id}${force ? '?force=1' : ''}`, {
    method: 'DELETE',
    headers: { Accept: 'application/json', ...authHeaders() },
  });
  if (res.status === 401) notifyUnauthorized();
  if (res.ok) return;
  const json = await res.json().catch(() => ({} as any));
  if (res.status === 409) {
    throw new BranchHasDealsError(Number(json?.deals_count ?? 0), json?.error || 'К филиалу привязаны игры');
  }
  throw new Error(json?.error || `HTTP ${res.status}`);
}
